/**
 * ServiceNowAIEditor
 *
 * Workflow node editor for the "ServiceNow AI" action.
 *
 * Architecture:
 *   ① AI Config         — which LLM + system prompt
 *   ② ServiceNow Config — which ServiceNow instance (credentials)
 *   ③ MCP Servers       — tools the AI can call
 *   ④ Scenario Prompts  — copy/paste snippets for the AI Config system prompt
 *   ⑤ Specialists       — optional delegation
 *
 * ServiceNow live tools (admin controller):
 *   - servicenow_get_incident         → GET /ServiceNow/Incidents/{id}
 *   - servicenow_search_incidents     → GET /ServiceNow/Incidents/Search
 *   - servicenow_create_incident      → POST /ServiceNow/Incidents
 *   - servicenow_create_request       → POST /ServiceNow/Requests
 *   - servicenow_search_knowledge     → GET /ServiceNow/Knowledge/Search
 *   - servicenow_search_changes       → GET /ServiceNow/Changes/Search
 *   - servicenow_create_change_request → POST /ServiceNow/Changes
 */
import * as React from "react";
import { useState } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useServiceNowConfigs from "@/hooks/useServiceNowConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

const SERVICENOW_SCENARIOS = [
  {
    id: "incident_lookup",
    label: "Incident Lookup",
    description: "Customer asks about an existing ticket or incident.",
    prompt: `When a customer asks about an existing ticket or incident, use servicenow_get_incident with the ticket number or servicenow_search_incidents by user email. Return the current status, assigned team, and expected resolution time.`
  },
  {
    id: "incident_creation",
    label: "Incident Creation",
    description: "Customer reports a new issue that needs a ticket logged.",
    prompt: `When a customer reports a new issue, collect the symptoms, urgency, and affected system. Use servicenow_create_incident to log it. Give the customer the incident number and SLA response time. Set priority based on the impact they describe.`
  },
  {
    id: "service_request",
    label: "Service Request",
    description: "Customer submits a service request for access, provisioning, or hardware.",
    prompt: `When a customer submits a service request (access, provisioning, hardware), use servicenow_create_request to log it in the catalog. Confirm the request details, provide the reference number, and explain the approval and fulfillment process.`
  },
  {
    id: "knowledge_search",
    label: "Knowledge Base Search",
    description: "Customer has a technical question — search knowledge articles first.",
    prompt: `When a customer has a technical question, first use servicenow_search_knowledge to find relevant articles. If a good article exists, summarize the key steps and provide the link. Only create an incident if the knowledge base has no answer.`
  },
  {
    id: "change_request",
    label: "Change Request",
    description: "Customer asks about a pending change or wants to request one.",
    prompt: `When someone asks about a pending change or wants to request a change, use servicenow_search_changes to find relevant CRs. For new requests, use servicenow_create_change_request and collect the justification, risk assessment, and implementation window.`
  }
];

export default function ServiceNowAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: serviceNowConfigs, loading: snLoading } = useServiceNowConfigs();
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const snOptions = serviceNowConfigs
    .filter((s) => s.enabled)
    .map((s) => ({
      value: s.id,
      label: s.displayName || s.instanceUrl || `Config #${s.id}`
    }));
  const allSnOptions = serviceNowConfigs.map((s) => ({
    value: s.id,
    label: (s.displayName || s.instanceUrl || `Config #${s.id}`) + (s.enabled ? "" : " (disabled)")
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* graceful fallback */ }
  }

  return (
    <>
      <AISetupGuide
        dataSourceName="ServiceNow"
        dataSourcePath="/integrations/servicenow"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={!!node.serviceNowConfigId}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found. <a href="/bots" className="underline font-medium">Create one first →</a>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            No system prompt yet. <a href="/bots" className="underline">Add one →</a> Use the Scenario Prompts below.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: ServiceNow Config ── */}
      <FormGroup label="ServiceNow Instance">
        {!snLoading && serviceNowConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No ServiceNow instance connected.{" "}
            <a href="/integrations/servicenow" className="underline font-medium">Connect an instance →</a>
            <p className="mt-1 text-slate-500">
              The ServiceNow config stores your instance URL and credentials so the AI can look up
              incidents, search knowledge, and create tickets.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select ServiceNow Instance —" },
                ...(snOptions.length > 0 ? snOptions : allSnOptions)
              ]}
              value={node.serviceNowConfigId ?? 0}
              onChange={(value) => onUpdate({ serviceNowConfigId: Number(value) || undefined })}
            />
            {serviceNowConfigs.length > 0 && snOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                All instances are disabled. <a href="/integrations/servicenow" className="underline">Enable one →</a>
              </p>
            )}
          </>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <a href="/integrations/mcp" className="text-blue-600 underline">Add an MCP server →</a>
            <p className="mt-1">
              MCP servers give the AI tools:{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">servicenow_get_incident</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">servicenow_create_incident</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">servicenow_search_knowledge</code>, etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && <span className="text-xs text-slate-500">{server.description}</span>}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">+{server.toolNames.length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario Prompts</span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. Customize the bracketed sections for your organization's specific ITSM processes.
        </p>
        <div className="flex flex-col gap-2">
          {SERVICENOW_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied ? "border-green-300 bg-green-50 text-green-700" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">Paste into your AI Config's system prompt, then customize the bracketed sections.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AIContextInspector mcpToolNames={enabledToolNames} />

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection node={node} onUpdate={onUpdate} aiConfigurations={aiConfigurations} enabledMcpServers={enabledMcpServers} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
